import React from 'react';
import { Briefcase, MapPin, Calendar, Clock } from 'lucide-react';
interface WorkExperience {
  company: string;
  roles: {
    title: string;
    type: string;
    startDate: string;
    endDate: string;
    location: string;
    description?: string[];
  }[];
}
const workHistory: WorkExperience[] = [{
  company: 'FiQIS',
  roles: [{
    title: 'Founder & CEO',
    type: 'Full-time',
    startDate: '2024',
    endDate: 'Present',
    location: 'Remote',
    description: ['Leading the development of next-generation financial infrastructure with post-quantum encryption', 'Architecting modular banking systems to replace legacy technology', 'Building smarter credit systems beyond traditional BNPL models', 'Fostering ethical innovation with transparency and stakeholder engagement']
  }]
}, {
  company: 'TrialNAV',
  roles: [{
    title: 'Fractional CTO | Technical Advisor',
    type: 'Part-time',
    startDate: 'Aug 2025',
    endDate: 'Present',
    location: 'Remote'
  }]
}, {
  company: 'Ocotillo Subscriptions',
  roles: [{
    title: 'Fractional CTO | Technical Advisor',
    type: 'Part-time',
    startDate: 'Jul 2025',
    endDate: 'Present',
    location: 'Arizona, United States · Remote'
  }]
}, {
  company: 'Plexus Worldwide',
  roles: [{
    title: 'Enterprise Architect',
    type: 'Full-time',
    startDate: 'Jun 2023',
    endDate: 'Present',
    location: 'Scottsdale, Arizona, United States · Hybrid'
  }]
}, {
  company: 'Forbes Technology Council',
  roles: [{
    title: 'Technology Council Member',
    type: 'Freelance',
    startDate: '2022',
    endDate: 'Sep 2025',
    location: 'Scottsdale, Arizona, United States · Remote'
  }]
}, {
  company: 'equipifi',
  roles: [{
    title: 'Co-Founder | Advisor',
    type: 'Part-time',
    startDate: '2023',
    endDate: '2024',
    location: 'Scottsdale, Arizona, United States',
    description: ['Advised on technical direction and implementation for sustainability of BNPL product and platform', 'Collaborated with team to ensure successful delivery of product features', 'Developed strategies to optimize platform performance and user experience']
  }, {
    title: 'Chief Technology Officer | Co-Founder | Member Board of Directors',
    type: 'Full-time',
    startDate: '2021',
    endDate: '2023',
    location: 'Scottsdale, Arizona, United States',
    description: ['Helped banks and credit unions grow and connect with members through buy now, pay later', 'Led Security Architecture, DevOps, Infrastructure Engineering, and Corporate Systems teams', 'Managed Delivery Management, QA/QC/QE, and Technology/Data/Identity Management and Compliance']
  }]
}, {
  company: 'Early Warning®',
  roles: [{
    title: 'Enterprise Solutions Architect - Enterprise Architecture Team (Zelle® Pay, RTP, and Platform)',
    type: 'Full-time',
    startDate: '2016',
    endDate: '2021',
    location: 'Scottsdale, Arizona · Hybrid'
  }]
}];
export function WorkHistorySection() {
  return <section id="work-history" className="py-24 bg-[#0B1120] px-4 sm:px-6 lg:px-8" data-id="element-243" data-mp="3eo5jh3t28m8z">
      <div className="max-w-5xl mx-auto" data-id="element-244" data-mp="30oea5gdhnu1b">
        <div className="text-center mb-16" data-id="element-245" data-mp="30pfnjrhz8sm7">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-246" data-mp="35mo63t71eynz">
            Work History
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-cyan-400 to-blue-500 mx-auto rounded-full mb-6" data-id="element-247" data-mp="yvyecje77jpc"></div>
          <p className="text-lg text-slate-400" data-id="element-248" data-mp="13ujiuv0hgpjl">
            A track record of leadership across fintech, enterprise
            architecture, and technology innovation.
          </p>
        </div>

        <div className="relative" data-id="element-249" data-mp="2uzcvrmmcs752">
          {/* Vertical Timeline Line */}
          <div className="absolute left-0 md:left-8 top-0 bottom-0 w-0.5 bg-white/10" data-id="element-250" data-mp="2q09tub1o6h1b"></div>

          <div className="space-y-12" data-id="element-251" data-mp="2lz836iltjday">
            {workHistory.map((experience, expIndex) => <div key={expIndex} className="relative" data-id="element-252" data-mp="2gzle9bwmo8mb" data-mpi={expIndex}>
                {experience.roles.map((role, roleIndex) => <div key={roleIndex} className={`relative ${roleIndex > 0 ? 'mt-8' : ''}`} data-id="element-253" data-mp="2c8v8hxkai3r3" data-mpi={roleIndex}>
                    {/* Timeline Dot */}
                    <div className="absolute left-0 md:left-8 transform -translate-x-1/2 flex items-center justify-center w-4 h-4 rounded-full bg-cyan-400 border-4 border-[#0B1120] shadow-sm z-10 glow-cyan" data-id="element-254" data-mp="22bh26qs2yvci"></div>

                    {/* Content Card */}
                    <div className="ml-8 md:ml-20" data-id="element-255" data-mp="1tcdjvp0qmjz2">
                      <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-cyan-500/30 hover:bg-white/10 transition-all" data-id="element-256" data-mp="8t1hv5rzj353">
                        {/* Company Name (only show once per company) */}
                        {roleIndex === 0 && <div className="flex items-center gap-2 mb-3" data-id="element-257" data-mp="msj7toh0omg1">
                            <Briefcase className="h-5 w-5 text-cyan-400" data-id="element-258" data-mp="3m74rzqse775n" />
                            <h3 className="text-xl font-bold text-white" data-id="element-259" data-mp="3m8zcx53nyz9q">
                              {experience.company}
                            </h3>
                          </div>}

                        {/* Role Title */}
                        <h4 className="text-lg font-semibold text-slate-200 mb-3" data-id="element-260" data-mp="3tez6gte0uow">
                          {role.title}
                        </h4>

                        {/* Meta Information */}
                        <div className="flex flex-wrap gap-4 text-sm text-slate-500 mb-4" data-id="element-261" data-mp="37hgsmv3ruikk">
                          <div className="flex items-center gap-1.5" data-id="element-262" data-mp="emynfpr24pq7">
                            <Calendar className="h-4 w-4 text-slate-600" data-id="element-263" data-mp="9oos9f5c1p4l" />
                            <span data-id="element-264" data-mp="9of29rkafhyw">
                              {role.startDate} - {role.endDate}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5" data-id="element-265" data-mp="jfjg2ny88ws5">
                            <Clock className="h-4 w-4 text-slate-600" data-id="element-266" data-mp="3ru0bsw72leib" />
                            <span data-id="element-267" data-mp="3rw4rsci8u3yy">{role.type}</span>
                          </div>
                          <div className="flex items-center gap-1.5" data-id="element-268" data-mp="3mx1jmj6yvhmr">
                            <MapPin className="h-4 w-4 text-slate-600" data-id="element-269" data-mp="4qn6gmgivbpf" />
                            <span data-id="element-270" data-mp="3vvdkrf5glxtg">{role.location}</span>
                          </div>
                        </div>

                        {/* Description Bullets */}
                        {role.description && role.description.length > 0 && <ul className="space-y-2 text-slate-400" data-id="element-271" data-mp="1b5efjdfsu2uj">
                            {role.description.map((item, idx) => <li key={idx} className="flex items-start gap-2" data-id="element-272" data-mp="1no9z178u71o6" data-mpi={idx}>
                                <span className="text-cyan-400 mt-1.5" data-id="element-273" data-mp="1nlxb8dgedx37">•</span>
                                <span className="flex-1" data-id="element-274" data-mp="19qbpcf5gntf0">{item}</span>
                              </li>)}
                          </ul>}
                      </div>
                    </div>
                  </div>)}
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}